import {useHistory, useParams} from 'react-router-dom';
import {Stack, Typography} from '@mui/material';
import GenericHeader from '../../../../../components/generic-header/generic-header';
import {translations} from '../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {useCallback, useEffect, useState} from 'react';
import {EntityOccurrencesRepository} from '../../../../../repositories/entity-occurrences';
import {EntityOccurrenceTypeCategory} from '../../../../../backend/responses/entity-occurrence';
import OccurrencesTable from './occurrences-table';
import AddOccurrenceModal from './add-occurrence-modal';
import {AddOccurrenceFormData} from '../../../daily-care-log/occurrences/add-occurrences-form';
import moment, {Moment} from 'moment';
import {useSnackBar} from '../../../../../contexts/snack-bar/context';
import OccurrencesTypesAddMenu from '../settings/occurrences-types/occurrences-types-add-menu';
import {useAuth} from '../../../../../contexts/auth/context';
import {RolePermissionType} from '../../../../../backend/enums/role-permission-type';
import {UsersInput} from '../../../../../components/users-input/users-input';
import {DateRangeInput} from '../../../../../components/date-range-input/date-range-input';
import {UsersFilters} from '../../../../../components/users-filters';
import HandleOccurrenceModal from './handle-occurrence-modal';
import {LocalStorageKey} from '../../../../../enums';
import {
  calcEndDateByRange,
  calcStartDateByRange,
  DateRangeType,
} from '../../../../../components/date-range-input/date-range-utils';
import {useEntity} from '../../../../../contexts/entity/context';
import {useAllSearchParams} from '../../../../../hooks/use-search-params';
import {createUsersFilters} from '../../../../../components/users-filters/users-filters-utils';
import {RESPONSIBLE_NOT_DEFINED} from '../professional-caregivers/shifts/actions-shift-flow/actions-shift-form';
import {TransformedOccurrence, transformOccurrences} from '../../../../../utils/data/occurrences';
import {UserType} from '../../../../../backend/enums/user-type';
import {useFetchEntityUsers} from '../../../../../hooks/use-fetch-entity-users';
import {requestAttachmentStorageUrl} from '../../../daily-care-log/unplanned-tasks/unplannedtask.daily-care-log.screen';
import {Attachment} from '../../../../../backend/interfaces/attachment';
import {StorageType} from '../../../../../backend/enums';
import {SavingAttachment} from '../../../../../components/daily-care-log/attachments/add-attachments-form';
import SaveIcon from '@mui/icons-material/SaveAlt';
import {useExportDataWithErrorHandler} from '../../../../../hooks/use-export-data-with-error-handler';
import {
  prepareOccurrencesPDF,
  prepareOccurrencesXLSX,
} from '../../../../../utils/data/export-data/occurences/export-data-occurences';
import {exportOccurencesGetReportData} from '../../../../../utils/data/export-data/occurences/base/export-occurences-get-report-data';
import eventEmitter from '../../../../../events/event-emitter';
import {
  PushNotificationReceived,
  PUSH_NOTIFICATION_RECEIVED,
} from '../../../../../events/event-types';
import {NotificationType} from '../../../../../backend/enums/notification-type';

export type OccurrenceStatus = 'registered' | 'handled' | 'not_handled';

const getInitialView = (occurrenceId?: string, occurrenceDate?: string) => {
  if (occurrenceId && occurrenceDate) return '1d';

  return dateRangeTypes.includes(String(localStorage.getItem(LocalStorageKey.OccurrencesRange)))
    ? (localStorage.getItem(LocalStorageKey.OccurrencesRange) as DateRangeType)
    : '1d';
};

export const dateRangeTypes = ['1d', '1w', '1m', '7d', '15d', '30d', '180d', '356d'];

export default function OccurrencesProfessionalCaregiverOfficeScreen() {
  const {t} = useTranslation();
  const snackBarContext = useSnackBar();
  const {hasWritePermission} = useAuth();
  const authContext = useAuth();
  const {
    updateEntityOccurrencesCount,
    updateEntityOccurrencesTypes,
    state: {occurrencesTypes},
  } = useEntity();
  const {entityId}: {entityId: string} = useParams();
  const history = useHistory();
  const {previewPdfWithErrorHandling, previewXlsxWithErrorHandling} =
    useExportDataWithErrorHandler();
  const searchParams = useAllSearchParams<{occurrenceId?: string; occurrenceDate?: string}>();
  const [occurrencesTableData, setOccurrencesTableData] = useState<TransformedOccurrence[]>();
  const [filteredOccurrences, setFilteredOccurrences] = useState<TransformedOccurrence[]>();
  const [isAddingOccurrence, setIsAddingOccurrence] = useState<EntityOccurrenceTypeCategory>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState<{
    user?: string;
    startDate: Moment;
    endDate: Moment;
    view: DateRangeType;
    socialResponses?: string[];
    status?: OccurrenceStatus[];
    patientOccurrences?: string[];
    generalOccurrences?: string[];
  }>({
    startDate:
      searchParams.occurrenceDate && searchParams.occurrenceId
        ? moment(searchParams.occurrenceDate).startOf('day')
        : calcStartDateByRange(getInitialView()),
    endDate:
      searchParams.occurrenceDate && searchParams.occurrenceId
        ? moment(searchParams.occurrenceDate).endOf('day')
        : calcEndDateByRange(getInitialView()),
    view: getInitialView(searchParams.occurrenceId, searchParams.occurrenceDate),
  });
  const [isOccurrenceModalOpen, setIsOccurrenceModalOpen] = useState<TransformedOccurrence>();
  const {items: users, isLoading: isLoadingUsers} = useFetchEntityUsers({activeUsers: true});
  const {items: professionals, isLoading: isLoadingProfessionals} = useFetchEntityUsers({
    userType: UserType.Caregiver,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const updateOccurrences = async () => {
      await updateEntityOccurrencesCount();
      await updateEntityOccurrencesTypes();
      setIsLoading(true);
    };

    updateOccurrences();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshOccurrences = useCallback(async () => {
    if (!entityId || isLoadingUsers || !occurrencesTypes || isLoadingProfessionals) {
      return;
    }

    setOccurrencesTableData(undefined);
    try {
      const occs = await EntityOccurrencesRepository.instance.getOccurrences(
        entityId,
        filters.startDate.toISOString(),
        filters.endDate.toISOString()
      );

      const data = await transformOccurrences(occs, occurrencesTypes, users, professionals);
      setOccurrencesTableData(data);
      setIsLoading(false);

      if (searchParams.occurrenceDate && searchParams.occurrenceId) {
        const find = data?.find((occurrence) => occurrence.id === searchParams.occurrenceId);
        if (find) setIsOccurrenceModalOpen(find);
      }
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  }, [
    entityId,
    filters.endDate,
    filters.startDate,
    isLoadingProfessionals,
    isLoadingUsers,
    occurrencesTypes,
    professionals,
    searchParams,
    users,
  ]);

  useEffect(() => {
    if (!isLoading) return;

    refreshOccurrences();
  }, [isLoading, refreshOccurrences]);

  useEffect(() => {
    const onPushNotificationReceived = (notification: PushNotificationReceived) => {
      if (
        notification.notificationType !== NotificationType.NewOccurrence &&
        notification.notificationType !== NotificationType.NewOccurrenceAssigned
      ) {
        return;
      }

      if (notification.entityId && notification.entityId !== entityId) {
        return;
      }

      setIsLoading(true);
    };

    eventEmitter.on(PUSH_NOTIFICATION_RECEIVED, onPushNotificationReceived);

    return () => {
      eventEmitter.off(PUSH_NOTIFICATION_RECEIVED, onPushNotificationReceived);
    };
  }, [entityId]);

  useEffect(() => {
    if (!occurrencesTableData || isLoading) return;

    const filtered = occurrencesTableData.filter((occurrence) => {
      const userMatch = !filters.user || occurrence.patient?.id === filters.user;
      const socialResponsesMatch =
        !filters.socialResponses ||
        occurrence.patient?.socialResponses.some((element) =>
          filters.socialResponses?.includes(element.id)
        );
      const statusMatch = !filters.status || filters.status.includes(occurrence.status);
      const patientsOccurrMatch = filters.patientOccurrences?.includes(occurrence.typeId);
      const generalOccurrMatch = filters.generalOccurrences?.includes(occurrence.typeId);

      const occurencesMatch =
        // if any filter is set, show only the ones that match
        patientsOccurrMatch ||
        generalOccurrMatch ||
        // if no filter is set, show all
        (!filters.patientOccurrences &&
          !patientsOccurrMatch &&
          !filters.generalOccurrences &&
          !generalOccurrMatch);

      return userMatch && socialResponsesMatch && statusMatch && occurencesMatch;
    });

    setFilteredOccurrences(filtered);
  }, [filters, isLoading, occurrencesTableData]);

  const handleAddOccurrence = async (values: AddOccurrenceFormData) => {
    if (Object.keys(values).length === 0) return;
    setIsSubmitting(true);
    let attachments: Attachment[] | undefined = undefined;

    if (values.occurrenceFiles) {
      const result = await requestAttachmentStorageUrl(
        entityId,
        values.occurrenceFiles,
        StorageType.OccurrenceAttachment
      );
      if (result === null) {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.daily_care_log.shift_details.unplanned_tasks.upload_error_toast),
        });
        return;
      } else {
        attachments = result;
      }
    }

    try {
      await EntityOccurrencesRepository.instance.addOccurrence(entityId, {
        typeId: values.occurrenceType,
        registeredAt: new Date(
          moment(values.occurrenceDate).year(),
          moment(values.occurrenceDate).month(),
          moment(values.occurrenceDate).date(),
          moment(values.occurrenceTime).hours(),
          moment(values.occurrenceTime).minutes(),
          moment(values.occurrenceTime).seconds()
        ),
        targetUserId: values.occurrencePatient ? values.occurrencePatient : undefined,
        responsibleUserId:
          values.occurrenceResponsible && values.occurrenceResponsible !== RESPONSIBLE_NOT_DEFINED
            ? values.occurrenceResponsible
            : undefined,
        siteName: values.occurrenceLocal.trim() || null,
        details: values.occurrenceDetails,
        attachments: attachments,
      });
      setIsSubmitting(false);
      setIsAddingOccurrence(undefined);
      setIsLoading(true);
      updateEntityOccurrencesCount();
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.daily_care_log.occurrences.add_success_toast),
      });
    } catch (e) {
      console.error('Error on add occurrence.', e);
      setIsSubmitting(false);
      setIsAddingOccurrence(undefined);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.daily_care_log.occurrences.add_error_toast),
      });
    }
  };

  const handleTakeNotes = async (id: string, notes: string) => {
    if (!entityId || !id) return;
    setIsSubmitting(true);

    try {
      await EntityOccurrencesRepository.instance.annotateOccurrence(entityId, id, notes);
      setIsSubmitting(false);
      setIsOccurrenceModalOpen(undefined);
      setIsLoading(true);
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.office.professional_caregiver.occurrences.notes_success),
      });
    } catch (e) {
      console.error('Error on taking notes.', e);
      setIsSubmitting(false);
      setIsOccurrenceModalOpen(undefined);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.office.professional_caregiver.occurrences.notes_error),
      });
    }
  };

  const editOccurrence = async (id: string, attachments: SavingAttachment[]) => {
    const find = occurrencesTableData?.find((occurrence) => occurrence.id === id);

    if (!find) {
      Promise.reject("Couldn't find the occurrence");
      return;
    }
    const result = await requestAttachmentStorageUrl(
      entityId,
      attachments,
      StorageType.OccurrenceAttachment
    );
    if (result === null) {
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.daily_care_log.shift_details.unplanned_tasks.upload_error_toast),
      });
      Promise.reject("Couldn't upload the attachments");
    } else {
      try {
        await EntityOccurrencesRepository.instance.editOccurrence(
          entityId,
          id,
          find.typeId,
          find.registeredAt,
          find.details,
          result
        );
      } catch (e) {
        console.error('Error on edit occurrence.', e);
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.daily_care_log.shift_details.unplanned_tasks.upload_error_toast),
        });
        Promise.reject("Couldn't edit the occurrence");
      }
    }
  };

  const handleOccurrence = async (
    id: string,
    handled: boolean,
    notes?: string,
    attachments?: SavingAttachment[]
  ) => {
    if (!entityId || !id) return;
    setIsSubmitting(true);

    if (attachments) {
      await editOccurrence(id, attachments);
    }

    try {
      await EntityOccurrencesRepository.instance.handleOccurrence(entityId, id, handled, notes);
      setIsSubmitting(false);
      setIsOccurrenceModalOpen(undefined);
      setIsLoading(true);
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.office.professional_caregiver.occurrences.handle_success),
      });
    } catch (e) {
      console.error('Error on handle occurrence.', e);
      setIsSubmitting(false);
      setIsOccurrenceModalOpen(undefined);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.office.professional_caregiver.occurrences.handle_error),
      });
    }
  };

  return (
    <Stack direction="column" sx={{gap: '15px'}}>
      <GenericHeader
        pageTitle={t(
          translations.office.professional_caregiver.settings.generic_header.breadcrumbs.occurrences
        )}
        isLoading={false}
        leftColumn={
          <Stack gap={2} sx={{display: 'flex', flexDirection: 'row', padding: 0, width: '100%'}}>
            <UsersInput
              onChange={(user) => {
                if (user?.user.id) {
                  setFilters({...filters, user: user.user.id});
                } else {
                  setFilters({...filters, user: undefined});
                }
              }}
            />
            <UsersFilters
              hideShifts
              hideTaskGroups
              hideActiveStatus
              taskStatus={[
                {
                  key: 'registered',
                  label: t(
                    translations.office.professional_caregiver.occurrences.status.registered
                  ),
                },
                {
                  key: 'not_handled',
                  label: t(
                    translations.office.professional_caregiver.occurrences.status.not_handled
                  ),
                },
                {
                  key: 'handled',
                  label: t(translations.office.professional_caregiver.occurrences.status.handled),
                },
              ]}
              onChange={(rawFilters) => {
                const fx = createUsersFilters(rawFilters);
                setFilters({
                  ...filters,
                  socialResponses: fx.socialResponses,
                  status: fx.taskStatus as OccurrenceStatus[],
                  patientOccurrences: fx.patientOccurrences,
                  generalOccurrences: fx.generalOccurrences,
                });
              }}
            />
          </Stack>
        }
        rightColumn={
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
            <DateRangeInput
              startDate={filters.startDate}
              endDate={filters.endDate}
              onChange={(startDate, endDate, range) => {
                setFilters({...filters, startDate, endDate, view: range});
                setIsLoading(true);
                if (range !== 'custom') {
                  localStorage.setItem(LocalStorageKey.OccurrencesRange, range);
                }
              }}
            />
            <OccurrencesTypesAddMenu
              canWrite={hasWritePermission(RolePermissionType.Occurrences)}
              onSelectType={(type) => setIsAddingOccurrence(type)}
            />
          </Stack>
        }
        actions={{
          actions: [
            {
              label: t(translations.office.generics.save_pdf),
              icon: {element: SaveIcon},
              callback: async () => {
                if (!filteredOccurrences || filteredOccurrences.length === 0) return;

                await previewPdfWithErrorHandling(async () => {
                  const entity = authContext.state.session?.selectedAffiliation?.entity;
                  const report = await exportOccurencesGetReportData({
                    entity,
                    filters,
                  });

                  if (!report) {
                    return null;
                  }

                  const pdf = prepareOccurrencesPDF({
                    reportData: report.reportData,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                    t,
                    entity,
                  });

                  return {
                    file: pdf.file,
                    fileName: pdf.fileName,
                  };
                });
              },
            },
            {
              label: t(translations.office.generics.save_xlsx),
              icon: {element: SaveIcon},
              callback: async () => {
                if (!filteredOccurrences || filteredOccurrences.length === 0) return;

                await previewXlsxWithErrorHandling(async () => {
                  const entity = authContext.state.session?.selectedAffiliation?.entity;
                  const report = await exportOccurencesGetReportData({
                    entity,
                    filters,
                  });

                  if (!report) {
                    return null;
                  }

                  const xlsx = prepareOccurrencesXLSX({
                    reportData: report.reportData,
                    entity,
                    t,
                  });

                  return {
                    file: xlsx.file,
                    fileName: xlsx.fileName,
                  };
                });
              },
            },
          ],
        }}
      />
      <div>
        {filteredOccurrences?.length === 0 ? (
          <Stack className="care-table-empty" justifyContent="center" alignItems="center">
            <h3 className="occurrences-empty-title">
              {t(translations.office.professional_caregiver.occurrences.empty_title)}
            </h3>
            <Typography>
              {t(translations.office.professional_caregiver.occurrences.empty_subtitle)}
            </Typography>
          </Stack>
        ) : (
          <OccurrencesTable
            isLoading={isLoadingUsers || isLoading}
            data={filteredOccurrences}
            onSelect={(id) => {
              const find = occurrencesTableData?.find((occurrence) => occurrence.id === id);
              if (find) setIsOccurrenceModalOpen(find);
            }}
          />
        )}
      </div>
      {isAddingOccurrence && (
        <AddOccurrenceModal
          users={users}
          professionals={professionals}
          submitting={isSubmitting}
          type={isAddingOccurrence}
          onAdd={handleAddOccurrence}
          onClose={() => setIsAddingOccurrence(undefined)}
        />
      )}
      {isOccurrenceModalOpen && (
        <HandleOccurrenceModal
          canEdit={hasWritePermission(RolePermissionType.Occurrences)}
          submitting={isSubmitting}
          data={isOccurrenceModalOpen}
          professionals={professionals}
          onClose={() => {
            setIsOccurrenceModalOpen(undefined);
            if (searchParams) {
              history.push({search: ''});
            }
          }}
          onHandle={async (id, handled, notes, attachments) => {
            handleOccurrence(id, handled, notes, attachments);

            if (searchParams) {
              history.push({search: ''});
            }
          }}
          onSaveNotes={(id, notes) => {
            handleTakeNotes(id, notes);
            if (searchParams) {
              history.push({search: ''});
            }
          }}
          onAcknowledge={() => setIsLoading(true)}
        />
      )}
    </Stack>
  );
}
